import { computed, onMounted, onUnmounted, ref, toValue } from 'vue'
import { useRouter } from 'vue-router'
import { useDebounceFn } from '@vueuse/core'
import { useParams } from '@deepcloud/deep-ui-utils/router'
import { useDeepBoxPreviewStore } from '@/stores/deepbox/deepboxes/preview.ts'
import type { MaybeRefOrGetter } from 'vue'

export type UsePreviewNavigationOptions = {
  /** Override current node id (default: route.params.node). */
  nodeId?: MaybeRefOrGetter<string | undefined>
  /** Override navigation (default: router.push to node-preview). */
  navigate?: (nodeId: string) => void
}

export function usePreviewNavigation(
  options: UsePreviewNavigationOptions = {},
) {
  const preventKeyboardNavigation = ref(false)
  const deepBoxPreviewStore = useDeepBoxPreviewStore()
  const router = useRouter()

  const params = useParams<{
    node: string
  }>()

  const resolvedNodeId = computed(
    () => toValue(options.nodeId) ?? params.value.node,
  )

  onMounted(() => {
    document.addEventListener('keyup', navigate)
  })

  onUnmounted(() => {
    document.removeEventListener('keyup', navigate)
  })

  const parentChildrenFiles = computed(
    () =>
      deepBoxPreviewStore.parentNodeChildren?.nodes?.filter(
        (node) => node.type === 'file',
      ) || [],
  )

  const totalFilesSize = computed(() => {
    return (
      deepBoxPreviewStore.parentNodeChildren?.size ??
      parentChildrenFiles.value.length
    )
  })

  const currentFileIndex = computed(() =>
    parentChildrenFiles.value.findIndex(
      (node) => node.nodeId === resolvedNodeId.value,
    ),
  )

  const nextNodeId = computed(
    () => parentChildrenFiles.value[currentFileIndex.value + 1]?.nodeId,
  )

  const previousNodeId = computed(
    () => parentChildrenFiles.value[currentFileIndex.value - 1]?.nodeId,
  )

  const nextItemRoute = computed(() => {
    return {
      name: 'node-preview',
      params: {
        node: nextNodeId.value,
      },
    }
  })

  const previousItemRoute = computed(() => {
    return {
      name: 'node-preview',
      params: {
        node: previousNodeId.value,
      },
    }
  })

  const pendingIndex = ref<number | null>(null)

  const displayIndex = computed(
    () => pendingIndex.value ?? currentFileIndex.value,
  )

  function goToNode(nodeId: string) {
    if (options.navigate) {
      options.navigate(nodeId)
      return
    }
    void router.push({ name: 'node-preview', params: { node: nodeId } })
  }

  const debouncedNavigate = useDebounceFn(() => {
    const idx = pendingIndex.value
    if (idx === null) return
    const node = parentChildrenFiles.value[idx]
    if (!node) return
    goToNode(node.nodeId)
    pendingIndex.value = null
  }, 300)

  function openNextFile() {
    const current = pendingIndex.value ?? currentFileIndex.value
    if (current !== -1 && current < parentChildrenFiles.value.length - 1) {
      pendingIndex.value = current + 1
      debouncedNavigate()
    }
  }

  function openPreviousFile() {
    const current = pendingIndex.value ?? currentFileIndex.value
    if (current > 0) {
      pendingIndex.value = current - 1
      debouncedNavigate()
    }
  }

  function navigate(event: KeyboardEvent) {
    if (preventKeyboardNavigation.value) return
    event.stopPropagation()
    if (event.key === 'ArrowRight') {
      openNextFile()
    } else if (event.key === 'ArrowLeft') {
      openPreviousFile()
    }
  }

  return {
    preventKeyboardNavigation,
    parentChildrenFiles,
    currentFileIndex,
    displayIndex,
    totalFilesSize,
    nextItemRoute,
    previousItemRoute,
    nextNodeId,
    previousNodeId,
    openNextFile,
    openPreviousFile,
  }
}
